<template>
	<div class="container pt-6 pb-10">
		<div class="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-2 py-2 mb-4">
			<div class="flex flex-row flex-wrap items-center gap-4 sm:gap-8">
				<v-btn color="grey darken-1" class="uppercase" dark to="/lucrari" exact>
					<v-icon>mdi-chevron-left</v-icon>
					Inapoi
				</v-btn>
				<!-- The stage, at the height of the buttons beside it -->
				<WorkStageChip v-if="work.stage && !noAccess" :stage="work.stage" class="stage-label" />
				<!-- second status: a finished work with open interventions -->
				<v-chip v-if="hasInterventie && !noAccess" :color="interventie.color" dark label class="stage-label"
					title="Interventii deschise">
					<v-icon left small>mdi-tools</v-icon>
					{{ interventie.text }} ({{ openInterventions }})
				</v-chip>
				<!-- second status: postponed by hand, keeps its stage -->
				<v-chip v-if="amanata && !noAccess" :color="amanataInfo.color" dark label class="stage-label"
					:title="work.motiv_amanare">
					<v-icon left small>mdi-pause-circle</v-icon>
					{{ amanataInfo.text }}
				</v-chip>
			</div>
			<div v-if="work.id_work && !noAccess" class="flex flex-row flex-wrap items-center gap-2 lg:justify-end">
				<v-btn v-if="canPostponeWork && amanata" color="brown" class="uppercase" dark :loading="savingAmanare"
					@click="saveAmanare(false)">
					<v-icon left>mdi-play-circle</v-icon>
					Reia lucrarea
				</v-btn>
				<v-btn v-if="canPostponeWork && !amanata && !isClosed(work.stage)" color="brown" class="uppercase" outlined
					@click="amanareDialog = true">
					<v-icon left>mdi-pause-circle</v-icon>
					Amana
				</v-btn>
				<v-btn v-if="nextAction && canAct && !amanata" color="teal" class="uppercase white--text"
					:disabled="blockedByDetalii" @click="confirmAction = true">
					<v-icon left>{{ nextAction.icon }}</v-icon>
					{{ nextAction.text }}
				</v-btn>
				<v-menu v-if="isAdmin" offset-y>
					<template v-slot:activator="{ on, attrs }">
						<v-btn color="grey darken-1" class="uppercase" dark v-bind="attrs" v-on="on">
							<v-icon left>mdi-pencil</v-icon>
							Corecteaza stadiu
						</v-btn>
					</template>
					<v-list dense>
						<v-list-item v-for="s in stages" :key="s.value" :disabled="s.value === work.stage"
							@click="forceStage(s.value)">
							<v-list-item-title>{{ s.text }}</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-menu>
			</div>
		</div>

		<h1 class="page-title-secondary mt-2 mb-4">
			Lucrare <template v-if="!noAccess">{{ work.client_nume }}</template>
			<span v-if="work.contract_id && !noAccess" class="text-gray-500 font-normal">- contract {{ work.contract_id
			}}</span>
		</h1>

		<div v-if="loadingWork && work.id_work != $route.params.id" class="text-center py-10">
			<v-progress-circular indeterminate color="primary" />
		</div>

		<v-alert v-else-if="noAccess" type="error" text>
			Nu aveti acces la aceasta lucrare: agentii vad doar lucrarile lor.
		</v-alert>

		<template v-else-if="work.id_work">
			<v-alert v-if="amanata" type="info" color="brown" text dense class="mb-4" icon="mdi-pause-circle">
				<b>Lucrare amanata</b> din {{ work.data_amanare | formatDate }}
				<template v-if="work.amanata_de_nume"> de {{ work.amanata_de_nume }}</template>:
				{{ work.motiv_amanare }}
				<div class="text-xs">Nu apare in lista de lucrari si nu poate fi programata pana este reluata.</div>
			</v-alert>
			<!-- Flow -->
			<v-stepper :value="currentStep" alt-labels class="flow-stepper mb-4 elevation-0 transparent">
				<v-stepper-header>
					<template v-for="(s, i) in flowStages">
						<v-stepper-step :key="s.value" :step="i + 1" :complete="stepComplete(i)" :color="s.color">
							<span class="text-center text-sm"
								:class="{ 'font-bold text-black': currentStep === i + 1 }">
								{{ s.text }}
							</span>
						</v-stepper-step>
						<v-divider v-if="i < flowStages.length - 1" :key="s.value + '-div'" />
					</template>
				</v-stepper-header>
			</v-stepper>
			<v-alert v-if="blockedByDetalii" type="warning" text>
				Detaliile lucrarii nu sunt stabilite, deci lucrarea nu poate trece in Gata de productie.
				<template v-if="canEditDetalii">
					Cand lamelele si culoarea sunt finale,
					<a href="#" class="font-bold" @click.prevent="setDetalii(true)">marcati detaliile ca stabilite</a>.
				</template>
			</v-alert>

			<!-- Client, documents, the work (wider): 2 / 2 / 3 of 7 columns -->
			<div class="grid grid-cols-1 lg:grid-cols-7 gap-4 mb-4">
				<!-- Client -->
				<v-card class="p-4 lg:col-span-2">
					<h2 class="text-lg font-bold mb-4">Client</h2>
					<div class="font-bold">{{ work.client_nume }}</div>
					<div v-if="work.client_tel">
						<v-icon small>mdi-phone</v-icon>
						<a :href="'tel:' + work.client_tel">{{ work.client_tel }}</a>
					</div>
					<div class="mt-2">
						<v-label>Locatie montaj</v-label>
						<div>{{ montageLocation }}</div>
					</div>
					<div class="mt-2">
						<v-label>Agent</v-label>
						<div>{{ work.agent | NoData }}</div>
					</div>
				</v-card>

				<!-- Documents -->
				<v-card class="p-4 lg:col-span-2">
					<h2 class="text-lg font-bold mb-4">Documente</h2>
					<div class="flex flex-col gap-1">
						<router-link :to="'/oferte-productie/single/' + work.oferta_id">
							<v-icon small>mdi-file-document</v-icon> Oferta {{ work.oferta_id }}
							<span v-if="acts.length" class="text-gray-600">(initiala)</span>
						</router-link>
						<router-link :to="'/productie/contracte/single/' + work.contract_id">
							<v-icon small>mdi-file-sign</v-icon> Contract {{ work.contract_id }}
							<span class="text-gray-600">din {{ work.data_contract | formatDate }}</span>
						</router-link>
						<!-- Each act replaces the offer: the modules and materials shown are the latest act's offer -->
						<div v-for="act in acts" :key="'act-' + act.id_aditional">
							<router-link :to="'/productie/contracte/single/' + work.contract_id + '/aditional/' + act.id_aditional">
								<v-icon small>mdi-file-replace</v-icon> Act aditional nr. {{ act.numar }}
								<span class="text-gray-600">din {{ act.data | formatDate }}</span>
							</router-link>
							&rarr;
							<router-link :to="'/oferte-productie/single/' + act.new_offer_id">
								Oferta {{ act.new_offer_id }}</router-link>
							<v-chip v-if="act.new_offer_id == work.oferta_curenta_id" x-small label color="primary" class="ml-1">
								curenta</v-chip>
						</div>
						<div>
							<v-icon small>mdi-invoice-text</v-icon> Proforma:
							<b>{{ work.paid_status || "nu exista" }}</b>
							<span v-if="work.data_avans" class="text-gray-600">
								(avans {{ work.data_avans | formatDate }})</span>
						</div>
						<div v-if="finisajeOk">
							<v-icon small color="green">mdi-wall</v-icon> Finisaje confirmate
							<span v-if="work.data_finisaje" class="text-gray-600">({{ work.data_finisaje | formatDate }})</span>
						</div>
						<div v-if="work.data_plata">
							<v-icon small color="green">mdi-cash-check</v-icon> Factura finala platita
							<span class="text-gray-600">({{ work.data_plata | formatDate }})</span>
						</div>
					</div>
					<!-- The measurement sheet (needed to confirm the measurements) and photos of the site -->
					<v-divider class="my-4"></v-divider>
					<WorkFiles :work="work" :editable="canEditFiles" />
				</v-card>

				<!-- The work: finishings, what is made, montage deadline -->
				<v-card class="p-4 lg:col-span-3">
					<h2 class="text-lg font-bold mb-4">Detalii lucrare</h2>
					<!-- Secondary status: are the slats and colour final -->
					<v-menu v-if="work.id_work && !noAccess" offset-y :disabled="!canEditDetalii">
						<template v-slot:activator="{ on, attrs }">
							<v-chip :color="detalii.color" outlined label class="flag-chip" v-bind="attrs" v-on="on"
								:title="canEditDetalii ? 'Modifica' : ''">
								<v-icon left small>{{ detalii.icon }}</v-icon>
								{{ detalii.text }}
								<v-icon v-if="canEditDetalii" right small>mdi-menu-down</v-icon>
							</v-chip>
						</template>
						<v-list dense>
							<v-list-item @click="setDetalii(!detaliiOk)">
								<v-list-item-title>
									Marcheaza {{ detaliiOk ? "nestabilite" : "stabilite" }}
								</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>
					<div class="grid grid-cols-2 lg:grid-cols-3 gap-2 mt-4">
						<div>
							<v-label>Culoare</v-label>
							<div>
								<RalSwatch :color="work.color" />
							</div>
						</div>
						<div>
							<v-label>Lamele</v-label>
							<div :title="slatPatternsTitle(work.slat_patterns)">
								{{ work.slat_patterns && work.slat_patterns.length ?
									slatPatternsLabel(work.slat_patterns) + " mm" :
									"-" }}
							</div>
						</div>
						<div>
							<v-label>Timp executie</v-label>
							<div>{{ work.t_executie | NoData }} zile</div>
						</div>
						<div>
							<v-label>Masuratori</v-label>
							<div>{{ includedText(work.masuratori_incluse, "Incluse") }}</div>
						</div>
						<div>
							<v-label>Montaj</v-label>
							<div>{{ includedText(work.montaj_inclus, "Inclus") }}</div>
						</div>
						<div>
							<v-label>Transport</v-label>
							<div>{{ includedText(work.transport_inclus, "Inclus") }}</div>
						</div>
						<!-- what the agent declared on the contract; confirmed by hand in the flow -->
						<div>
							<v-label>Finisaje (contract)</v-label>
							<div>{{ agentFinisajeText(work) }}</div>
						</div>
					</div>
					<!-- Montage deadline -->
					<div class="mt-4 pt-4 border-t border-gray-200">
						<div class="flex flex-row justify-between items-center mb-2">
							<v-label>Data montaj maxima</v-label>
							<v-menu v-if="isAdmin" v-model="deadlineMenu" :close-on-content-click="false" left offset-y>
								<template v-slot:activator="{ on, attrs }">
									<v-btn icon small v-bind="attrs" v-on="on"><v-icon small>mdi-pencil</v-icon></v-btn>
								</template>
								<v-card class="p-2">
									<v-date-picker v-model="deadlineInput" no-title first-day-of-week="1" locale="ro" />
									<div class="flex flex-row gap-2 px-2 pb-2">
										<v-btn small color="teal" dark
											@click="saveDeadline(deadlineInput)">Salveaza</v-btn>
										<v-btn v-if="work.deadline_manual == 1" small text @click="saveDeadline(null)">
											Recalculeaza
										</v-btn>
									</div>
								</v-card>
							</v-menu>
						</div>
						<div v-if="work.data_montaj_maxima">
							<div class="text-2xl font-bold">{{ work.data_montaj_maxima | formatDate }}</div>
							<div v-if="!isClosed(work.stage)" class="font-bold" :class="deadlineColorClass">{{
								deadlineText }}</div>
						</div>
						<div v-else class="text-gray-600">-</div>
						<div class="text-sm text-gray-600 mt-2">
							<template v-if="work.deadline_manual == 1">Setata manual.</template>
							<template v-else>
								{{ work.deadline_days }} zile de la data contractului (termenul de executie din oferta).
							</template>
						</div>
					</div>
				</v-card>
			</div>

			<!-- Bookings per calendar -->
			<h2 class="section-title mt-6 mb-3">Programari</h2>
			<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
				<v-card v-for="cal in calendars" :key="cal.type" class="p-4">
					<div class="flex flex-row justify-between items-center mb-4">
						<h2 class="text-lg font-bold">
							<v-icon class="mr-1">{{ cal.icon }}</v-icon>
							<router-link :to="'/lucrari/calendar/' + cal.type" class="text-black">
								{{ cal.type | capitalize }}
							</router-link>
						</h2>
						<v-btn v-if="canBookType(cal.type)" small color="teal" dark @click="openCreate(cal.type)">
							<v-icon left small>mdi-calendar-plus</v-icon>
							Programeaza
						</v-btn>
					</div>
					<p v-if="!eventsOf(cal.type).length" class="text-sm text-gray-600 mb-0">Neprogramat.</p>
					<v-list dense class="p-0">
						<v-list-item v-for="e in eventsOf(cal.type)" :key="e.id_event" class="px-0"
							@click="openEdit(cal.type, e)">
							<v-list-item-content>
								<v-list-item-title>{{ eventWhen(e) }}</v-list-item-title>
								<v-list-item-subtitle v-if="e.assignees.length">
									{{e.assignees.map((a) => a.name).join(", ")}}
								</v-list-item-subtitle>
								<v-list-item-subtitle v-if="e.notes">{{ e.notes }}</v-list-item-subtitle>
							</v-list-item-content>
						</v-list-item>
					</v-list>
				</v-card>
			</div>

			<!-- Painting, observations, interventions -->
			<h2 class="section-title mt-6 mb-3">Detalii productie</h2>
			<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
				<!-- External painting: the whole work and/or single elements (e.g. the posts), each with
				     when it was sent to the painter and when it came back -->
				<v-card class="p-4">
					<div class="flex justify-between items-start mb-4">
						<h2 class="text-lg font-bold">
							<v-icon class="mr-1">mdi-spray</v-icon>
							Vopsire externa
						</h2>
						<v-chip v-if="vopsireStatus" small label outlined class="flag-chip" :color="vopsireStatus.color">
							{{ vopsireStatus.text }}
						</v-chip>
					</div>

					<v-switch :input-value="vopsire" class="mt-0" label="Toata lucrarea" inset hide-details
						:disabled="!canEditVopsire || savingVopsire" @change="saveVopsire({ vopsire_externa: !!$event })"></v-switch>
					<div v-if="vopsire" class="grid grid-cols-2 gap-2 mt-3">
						<div>
							<v-switch :input-value="!!work.data_vopsire_trimisa" class="mt-0" label="Trimisa" inset
								hide-details color="blue" :disabled="!canEditVopsire || savingVopsire"
								@change="saveVopsire({ vopsire_trimisa: !!$event })"></v-switch>
							<div v-if="work.data_vopsire_trimisa" class="text-xs text-gray-600 mt-1 ml-12">
								din {{ work.data_vopsire_trimisa | formatDate }}</div>
						</div>
						<div>
							<v-switch :input-value="!!work.data_vopsire_returnata" class="mt-0" label="Returnata" inset
								hide-details color="green"
								:disabled="!canEditVopsire || savingVopsire || !work.data_vopsire_trimisa"
								@change="saveVopsire({ vopsire_returnata: !!$event })"></v-switch>
							<div v-if="work.data_vopsire_returnata" class="text-xs text-gray-600 mt-1 ml-12">
								din {{ work.data_vopsire_returnata | formatDate }}</div>
						</div>
					</div>

					<div class="mt-4 pt-4 border-t border-gray-200">
						<!-- the agent may list the elements too; the switches are production's -->
						<template v-if="canEditElemente">
							<v-textarea v-model="vopsireElemente" label="Extra elemente" rows="2" auto-grow
								outlined dense hide-details placeholder="ex. stalpi, porti, accesorii"></v-textarea>
							<div v-if="vopsireElementeChanged" class="flex flex-row gap-2 mt-2">
								<v-btn small color="teal" dark :loading="savingVopsire"
									@click="saveVopsire({ vopsire_elemente: vopsireElemente })">Salveaza</v-btn>
								<v-btn small text @click="vopsireElemente = work.vopsire_elemente || ''">Anuleaza</v-btn>
							</div>
						</template>
						<template v-else>
							<v-label>Extra elemente</v-label>
							<div class="whitespace-pre-line">{{ work.vopsire_elemente || "-" }}</div>
						</template>
						<div v-if="work.vopsire_elemente" class="grid grid-cols-2 gap-2 mt-3">
							<div>
								<v-switch :input-value="!!work.data_elemente_trimise" class="mt-0" label="Trimise" inset
									hide-details color="blue" :disabled="!canEditVopsire || savingVopsire"
									@change="saveVopsire({ elemente_trimise: !!$event })"></v-switch>
								<div v-if="work.data_elemente_trimise" class="text-xs text-gray-600 mt-1 ml-12">
									din {{ work.data_elemente_trimise | formatDate }}</div>
							</div>
							<div>
								<v-switch :input-value="!!work.data_elemente_returnate" class="mt-0" label="Returnate" inset
									hide-details color="green"
									:disabled="!canEditVopsire || savingVopsire || !work.data_elemente_trimise"
									@change="saveVopsire({ elemente_returnate: !!$event })"></v-switch>
								<div v-if="work.data_elemente_returnate" class="text-xs text-gray-600 mt-1 ml-12">
									din {{ work.data_elemente_returnate | formatDate }}</div>
							</div>
						</div>
					</div>
				</v-card>

				<!-- Observations, and interventions (problems the client reports, mostly after the work is finished) -->
				<WorkNotesCard :work="work" kind="observatie" />
				<WorkNotesCard :work="work" kind="interventie" />
			</div>

			<!-- What production makes -->
			<v-card class="p-4">
				<h2 class="text-lg font-bold mb-4">Module gard</h2>
				<v-simple-table v-if="work.gard_obj && work.gard_obj.length" dense>
					<thead>
						<tr>
							<th>Nr</th>
							<th>Tip</th>
							<th>Model</th>
							<th>Latime x Inaltime</th>
							<th>Lamele</th>
							<th>Buc</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(m, i) in work.gard_obj" :key="i">
							<td>{{ m.nr || i + 1 }}</td>
							<td>{{ moduleType(m) }}</td>
							<td>{{ m.denumire_model || m.model }}</td>
							<td>{{ m.latime }} x {{ m.inaltime }}</td>
							<td>
								<span v-if="m.slat_widths_resolved && m.slat_widths_resolved.length">
									<template v-if="m.nr_profile">{{ m.nr_profile }} x </template>{{
										m.slat_widths_resolved.join("/") }} mm
								</span>
							</td>
							<td>{{ m.buc }}</td>
						</tr>
					</tbody>
				</v-simple-table>
				<p v-else class="text-sm text-gray-600 mb-0">Oferta nu are module de gard.</p>
			</v-card>

			<!-- admins only while the BOM rules are being confirmed -->
			<v-card v-if="isAdmin && work.gard_obj && work.gard_obj.length" class="p-4 mt-4">
				<FenceBom :work-id="work.id_work" title-class="text-lg font-bold" />
			</v-card>
		</template>

		<!-- Postpone: the reason is required -->
		<v-dialog v-model="amanareDialog" max-width="460px">
			<v-card class="p-8">
				<h6 class="text-xl font-bold mb-2">Amana lucrarea?</h6>
				<p class="text-sm mb-4">Lucrarea isi pastreaza stadiul. Iese din lista de lucrari si din listele
					"De programat" pana este reluata.</p>
				<v-textarea v-model="motivAmanare" label="Motivul amanarii" rows="2" auto-grow outlined counter="500"
					:rules="[(v) => !!(v && v.trim()) || 'Scrieti motivul']"></v-textarea>
				<div class="flex flex-row justify-end gap-2 mt-2">
					<v-btn color="brown" class="uppercase" dark :loading="savingAmanare"
						:disabled="!motivAmanare.trim() || motivAmanare.length > 500" @click="saveAmanare(true)">Amana</v-btn>
					<v-btn color="blue-grey" class="uppercase" dark @click="amanareDialog = false">Anuleaza</v-btn>
				</div>
			</v-card>
		</v-dialog>

		<!-- Confirm stage action -->
		<v-dialog v-model="confirmAction" max-width="420px">
			<v-card v-if="nextAction" class="p-8">
				<h6 class="text-xl font-bold mb-4">{{ nextAction.text }}?</h6>
				<!-- the sheet is required: upload it here or in the card -->
				<div v-if="nextAction.action === 'masuratori_luate'" class="mb-4">
					<v-alert v-if="!hasMasuratoriDoc" type="warning" text dense class="text-sm">
						Incarcati fisa de masuratori (o poza sau un PDF) pentru a confirma.
					</v-alert>
					<WorkFiles :work="work" :kinds="['masuratori']" :editable="canEditFiles" />
				</div>
				<div v-if="nextAction.action === 'finisaje_confirmate'" class="text-sm mb-4">
					<p class="mb-2">Finisajele clientului sunt gata? Lucrarea trece la asteptare masuratori finale.</p>
					<div><b>Declarat de agent la contract:</b> {{ agentFinisajeText(work) }}</div>
				</div>
				<p v-if="nextAction.action === 'marcheaza_platita'" class="text-sm">
					Clientul a achitat factura finala dupa montaj.
				</p>
				<v-form v-if="nextAction.action === 'montaj_finalizat'" ref="montajForm">
					<v-radio-group v-model="montaj.probleme" class="mt-0"
						:rules="[(v) => v !== null || 'Alegeti o varianta']">
						<v-radio label="Fara probleme" :value="false" color="teal"></v-radio>
						<v-radio label="Cu probleme de montaj" :value="true" color="orange"></v-radio>
					</v-radio-group>
					<v-textarea v-if="montaj.probleme" v-model="montaj.observatii" label="Observatii" rows="3" auto-grow
						outlined :rules="[(v) => !!(v && v.trim()) || 'Descrieti problemele de montaj']"></v-textarea>
				</v-form>
				<div class="flex flex-row gap-2">
					<v-btn color="teal" class="uppercase" :dark="!blockedByFiles" :disabled="blockedByFiles" :loading="acting"
						@click="runAction">Confirma</v-btn>
					<v-btn color="blue-grey" class="uppercase" dark @click="confirmAction = false">Anuleaza</v-btn>
				</div>
			</v-card>
		</v-dialog>

		<WorkEventDialog v-if="dialog.type" v-model="dialog.open" :type="dialog.type" :event="dialog.event"
			:fixed-work="work" :read-only="dialog.readOnly" @saved="reload" />
	</div>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import {
	STAGES, nextAction, CALENDARS, canBook, stageIndex, daysToDeadline, deadlineColor, formatHours,
	finisajeDone, agentFinisajeText, detaliiInfo, detaliiDone, canChangeDetalii, slatPatternsLabel, slatPatternsTitle, isClosed, INTERVENTIE, openInterventions, inInterventie, isWorksAgent, isAgentsWork, agentCanChange, AMANATA, isAmanata, canPostpone, montageLocation, needsVopsire, canChangeVopsire, vopsireState, vopsireStateText,
} from "@/utils/works";
import WorkStageChip from "./WorkStageChip";
import RalSwatch from "./RalSwatch";
import WorkEventDialog from "./WorkEventDialog";
import WorkNotesCard from "./WorkNotesCard";
import WorkFiles from "./WorkFiles";
import FenceBom from "@/components/FenceBom/FenceBom";

const MODULE_TYPES = {
	Panou: "Panou",
	Panou_rama: "Panou rama",
	Poarta_mica: "Poarta pietonala",
	Poarta_mare: "Poarta mare",
	Poarta_batanta: "Poarta batanta",
	Poarta_culisanta: "Poarta culisanta",
	Poarta_autoportanta: "Poarta autoportanta",
	Stalp: "Stalp",
};

export default {
	name: "WorkDetails",
	components: { WorkStageChip, RalSwatch, WorkEventDialog, FenceBom, WorkNotesCard, WorkFiles },
	filters: {
		capitalize(v) {
			return v ? v.charAt(0).toUpperCase() + v.slice(1) : v;
		},
	},
	data() {
		return {
			confirmAction: false,
			acting: false,
			amanareDialog: false,
			motivAmanare: "",
			savingAmanare: false,
			amanataInfo: AMANATA,
			// the "Alte elemente de vopsit" field while it is being edited
			vopsireElemente: "",
			savingVopsire: false,
			// how the montage went, asked when marking it finished
			montaj: { probleme: null, observatii: "" },
			deadlineMenu: false,
			deadlineInput: null,
			dialog: { open: false, type: null, event: null, readOnly: false },
		};
	},
	computed: {
		...mapState("works", ["work", "loadingWork"]),
		user() {
			return this.$store.getters.loggedUser;
		},
		isAdmin() {
			return ["admin", "superuser"].includes(this.user.rol);
		},
		isAgent() {
			return isWorksAgent(this.user);
		},
		// the agent's own work - for an export agent, any export work
		isOwn() {
			return isAgentsWork(this.user, this.work);
		},
		// Agents only see their works.
		noAccess() {
			return this.isAgent && !!this.work.id_work && !this.isOwn;
		},
		// Corecteaza stadiu
		stages() {
			return STAGES;
		},
		flowStages() {
			return STAGES;
		},
		currentStep() {
			const i = stageIndex(this.work.stage);
			// a paid work shows every step complete
			return this.work.stage === "platita" ? STAGES.length + 1 : i + 1;
		},
		interventie() {
			return INTERVENTIE;
		},
		openInterventions() {
			return openInterventions(this.work);
		},
		hasInterventie() {
			return inInterventie(this.work);
		},
		amanata() {
			return isAmanata(this.work);
		},
		canPostponeWork() {
			return canPostpone(this.user);
		},
		calendars() {
			return Object.values(CALENDARS);
		},
		nextAction() {
			return nextAction(this.work);
		},
		// Agents confirm the sales side of the flow on their own works; production the rest.
		detalii() {
			return detaliiInfo(this.work);
		},
		vopsire() {
			return needsVopsire(this.work);
		},
		vopsireStatus() {
			const state = vopsireState(this.work);
			if (!state) return null;
			const colors = { de_trimis: "amber darken-3", la_vopsit: "blue", returnata: "green" };
			return { text: vopsireStateText(this.work), color: colors[state] };
		},
		// the switches: production, superusers and admins
		canEditVopsire() {
			return canChangeVopsire(this.user);
		},
		// the extra elements' text: also the agent of the work, until the elements are sent (clearing
		// the list would clear their dates)
		canEditElemente() {
			return this.canEditVopsire || (this.isAgent && this.isOwn && !this.work.data_elemente_trimise);
		},
		vopsireElementeChanged() {
			return this.vopsireElemente.trim() !== (this.work.vopsire_elemente || "").trim();
		},
		finisajeOk() {
			return finisajeDone(this.work);
		},
		detaliiOk() {
			return detaliiDone(this.work);
		},
		// Measurements done but the details not set: it can't go to production.
		blockedByDetalii() {
			return this.work.stage === "asteapta_masuratori" && !this.detaliiOk;
		},
		acts() {
			return this.work.acte_aditionale || [];
		},
		hasMasuratoriDoc() {
			return (this.work.files || []).some((f) => f.kind === "masuratori");
		},
		blockedByFiles() {
			return this.nextAction && this.nextAction.action === "masuratori_luate" && !this.hasMasuratoriDoc;
		},
		// Files can be added at any stage: agents on their own works, and production/admins.
		canEditFiles() {
			if (this.isAgent) return this.isOwn;
			return ["admin", "superuser", "user"].includes(this.user.rol);
		},
		// Agents on their own works, and production/admins, before the final measurements.
		canEditDetalii() {
			if (!canChangeDetalii(this.work)) return false;
			if (this.isAgent) return this.isOwn;
			return ["admin", "superuser", "user"].includes(this.user.rol);
		},
		canAct() {
			if (this.nextAction.adminOnly) return this.isAdmin;
			if (this.isAgent) {
				return this.isOwn && ["avans_incasat", "finisaje_confirmate", "masuratori_luate"].includes(this.nextAction.action);
			}
			return ["admin", "superuser", "user"].includes(this.user.rol);
		},
		montageLocation() {
			return montageLocation(this.work) || "-";
		},
		deadlineDays() {
			return daysToDeadline(this.work);
		},
		deadlineText() {
			const days = this.deadlineDays;
			if (days < 0) return `Depasit cu ${-days} zile`;
			if (days === 0) return "Astazi";
			return `Peste ${days} zile`;
		},
		deadlineColorClass() {
			return deadlineColor(this.deadlineDays) + "--text";
		},
	},
	watch: {
		// the field follows the saved text when the work (re)loads or is saved
		"work.vopsire_elemente": {
			handler(text) {
				this.vopsireElemente = text || "";
			},
			immediate: true,
		},
		confirmAction(open) {
			if (open) {
				this.montaj = { probleme: null, observatii: "" };
				this.$nextTick(() => this.$refs.montajForm && this.$refs.montajForm.resetValidation());
			}
		},
		"$route.params.id"() {
			this.reload();
		},
		deadlineMenu(open) {
			if (open) this.deadlineInput = this.work.data_montaj_maxima;
		},
	},
	methods: {
		stepComplete(i) {
			return this.currentStep > i + 1;
		},
		reload() {
			return this.$store.dispatch("works/fetchWork", this.$route.params.id).catch(() => { });
		},
		eventsOf(type) {
			return (this.work.events || []).filter((e) => e.type === type);
		},
		canEditType(type) {
			// agents: measurements of their own works, until the work reaches production
			if (type === "masuratori") return !this.isAgent || (this.isOwn && agentCanChange(this.work));
			return !this.isAgent && ["admin", "superuser", "user"].includes(this.user.rol);
		},
		canBookType(type) {
			return this.canEditType(type) && canBook(this.work, type);
		},
		openCreate(type) {
			this.dialog = { open: true, type, event: null, readOnly: false };
		},
		openEdit(type, event) {
			this.dialog = { open: true, type, event, readOnly: !this.canEditType(type) };
		},
		eventWhen(e) {
			const start = moment(e.start);
			if (e.all_day != 1) {
				// production: start - end over working days, and its hours
				const end = moment(e.end);
				const span = start.isSame(end, "day")
					? start.format("DD.MM.YYYY HH:mm") + " - " + end.format("HH:mm")
					: start.format("DD.MM HH:mm") + " - " + end.format("DD.MM.YYYY HH:mm");
				return e.hours ? `${span} (${formatHours(e.hours)})` : span;
			}
			const last = moment(e.end).subtract(1, "minute");
			return start.isSame(last, "day")
				? start.format("DD.MM.YYYY")
				: start.format("DD.MM") + " - " + last.format("DD.MM.YYYY");
		},
		async runAction() {
			const details = {};
			if (this.nextAction.action === "montaj_finalizat") {
				if (!this.$refs.montajForm.validate()) return;
				details.montaj_probleme = this.montaj.probleme ? 1 : 0;
				details.montaj_observatii = this.montaj.probleme ? this.montaj.observatii.trim() : null;
			}
			this.acting = true;
			try {
				await this.$store.dispatch("works/changeStage", {
					id: this.work.id_work,
					action: this.nextAction.action,
					...details,
				});
				this.confirmAction = false;
				await this.reload();
			} catch (e) {
				// toast already shown by the store
			} finally {
				this.acting = false;
			}
		},
		slatPatternsLabel,
		agentFinisajeText,
		slatPatternsTitle,
		isClosed,
		async saveVopsire(fields) {
			this.savingVopsire = true;
			await this.$store.dispatch("works/updateVopsire", { id: this.work.id_work, ...fields }).catch(() => { });
			this.savingVopsire = false;
		},
		async saveAmanare(amanata) {
			if (!amanata && !confirm("Reiei lucrarea? Revine in lista de lucrari si poate fi programata.")) return;
			this.savingAmanare = true;
			const ok = await this.$store
				.dispatch("works/updateAmanare", { id: this.work.id_work, amanata, motiv: this.motivAmanare })
				.then(() => true, () => false);
			this.savingAmanare = false;
			if (ok && amanata) {
				this.amanareDialog = false;
				this.motivAmanare = "";
			}
		},
		async setDetalii(done) {
			await this.$store.dispatch("works/updateDetalii", { id: this.work.id_work, done }).catch(() => { });
		},
		async forceStage(stage) {
			if (!confirm("Schimbi manual stadiul lucrarii? Programarile existente raman neschimbate.")) return;
			await this.$store.dispatch("works/changeStage", { id: this.work.id_work, action: "force", stage }).catch(() => { });
			this.reload();
		},
		async saveDeadline(date) {
			await this.$store.dispatch("works/updateDeadline", { id: this.work.id_work, date }).catch(() => { });
			this.deadlineMenu = false;
		},
		includedText(value, included) {
			if (value == 1) return included;
			if (value == 2) return "Contra cost";
			return "Nu";
		},
		moduleType(m) {
			return MODULE_TYPES[m.tip] || m.tip;
		},
	},
	created() {
		this.reload();
	},
};
</script>

<style scoped>
/* Both at 36px, the height of the header buttons (Inapoi, Corecteaza stadiu). */
.stage-label.v-chip {
	height: 36px !important;
	padding: 0 16px;
}


/* Vuetify clears outlined chips with ".v-chip.v-chip--outlined.v-chip.v-chip { background-color:
   transparent !important }" - its own selector plus our class is needed to win over it. */
.v-chip.v-chip--outlined.v-chip.v-chip.flag-chip {
	background-color: #fff !important;
}

/* The flow uses the page's width: Vuetify gives each labelled step 175px plus 24px padding, which
   leaves the first and last circle ~80px in from the edges. Narrower steps (the labels wrap) put
   them ~48px in; each divider's negative margins are pulled in to match, so the line still runs
   from circle to circle (half a step, less the 12px circle radius and an 8px gap). Below 960px
   Vuetify hides the labels and lays the steps out its own way. */
@media (min-width: 960px) {
	.flow-stepper ::v-deep .v-stepper__step {
		flex-basis: 96px;
		padding: 24px 4px;
	}

	.flow-stepper ::v-deep .v-stepper__header .v-divider {
		margin: 35px -28px 0;
	}
}

/* Labels centred under their circle: Vuetify makes the label a left-aligned block (the
   text-center on the span inside is inline and does nothing), shrunk to its text. */
.flow-stepper ::v-deep .v-stepper__label {
	width: 100%;
	text-align: center;
}
</style>
